import React from 'react';

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DateTimePicker } from 'react-datetime-picker';

import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';

// Use string constants instead of enum to avoid unsafe comparisons
const CUSTOM_RANGE = 'custom';

type QuickRangeType = string;

type DateTimeRangePickerProps = {
  isDateRangeOpened: boolean;
  setIsDateRangeOpened: (value: boolean) => void;
  quickRangeApplied: null | Array<Date | null>;
  quickRangeSelected: QuickRangeType;
  setStartDateTime: (value: Date | null) => void;
  startDateTime: Date | null;
  setEndDateTime: (value: Date | null) => void;
  endDateTime: Date | null;
  applyDateTimeRange: () => void;
  setDateTimeRangeFromQuickRange: (quickRange: QuickRangeType) => void;
};

// Generate dynamic year ranges: max 5 years, no earlier than 2023
const generateYearRanges = (): string[] => {
  const currentYear = new Date().getFullYear();
  const minYear = 2023;
  const maxYears = 5;
  const years: string[] = [];

  for (let i = 0; i < maxYears; i++) {
    const year = currentYear - i;
    if (year >= minYear) {
      years.push(year.toString());
    } else {
      break; // Stop if we reach a year earlier than 2023
    }
  }

  return years;
};

// Type guard to check if value is a year string
const isYearString = (value: string): boolean => {
  const num = Number(value);
  return !isNaN(num) && num > 1900 && num < 3000; // reasonable year range
};

export const getQuickRange = (quickRange: QuickRangeType): [Date, Date] => {
  let year: number;

  // Check if it's a year string
  if (quickRange !== CUSTOM_RANGE && isYearString(quickRange)) {
    year = Number(quickRange);
  } else {
    year = new Date().getFullYear();
  }

  const startDateTime = new Date(year, 0, 1, 0, 0, 0); // January 1st, 00:00:00
  const endDateTime = new Date(year, 11, 31, 23, 59, 59); // December 31st, 23:59:59

  return [startDateTime, endDateTime];
};

const getQuickRangeFilterMessage = (quickRange: QuickRangeType): string => {
  if (quickRange === CUSTOM_RANGE) {
    return 'Custom Range';
  }

  // If it's a year string, return the year
  if (isYearString(quickRange)) {
    return quickRange;
  }

  return '';
};

// Validation function to check if dates are within the same year
const validateSameYear = (
  startDate: Date | null,
  endDate: Date | null
): boolean => {
  if (!startDate || !endDate) return true; // Allow null dates
  return startDate.getFullYear() === endDate.getFullYear();
};

export default function DateTimeRangePicker({
  isDateRangeOpened,
  setIsDateRangeOpened,
  quickRangeApplied,
  quickRangeSelected,
  setStartDateTime,
  startDateTime,
  setEndDateTime,
  endDateTime,
  applyDateTimeRange,
  setDateTimeRangeFromQuickRange,
}: DateTimeRangePickerProps) {
  // Generate dynamic year ranges
  const yearRanges = generateYearRanges();

  // Check if current date range is valid (same year)
  const isValidRange = validateSameYear(startDateTime, endDateTime);

  const handleApplyDateTimeRange = () => {
    if (!isValidRange) {
      alert('Please select dates within the same year.');
      return;
    }
    applyDateTimeRange();
  };

  return (
    <div className={'p-2'}>
      <div
        className={'flex'}
        onClick={() => setIsDateRangeOpened(!isDateRangeOpened)}
      >
        <div className={'flex-none pr-4'}>
          {quickRangeApplied &&
            quickRangeApplied[0] &&
            quickRangeApplied[1] && (
              <span>
                <span className={'font-bold'}>From: </span>{' '}
                {quickRangeApplied[0]
                  ? new Date(quickRangeApplied[0]).toUTCString()
                  : ''}{' '}
                <span className={'font-bold'}>To: </span>
                {quickRangeApplied[1]
                  ? new Date(quickRangeApplied[1]).toUTCString()
                  : ''}
              </span>
            )}
          {!quickRangeApplied && quickRangeSelected && (
            <>
              <span>Time Range: </span>
              <span className={'font-bold'}>
                {getQuickRangeFilterMessage(quickRangeSelected)}
              </span>
            </>
          )}
        </div>
        <div className={'flex-auto'}>
          {isDateRangeOpened ? (
            <span>
              <ChevronUpIcon className="h-6 w-6" />
            </span>
          ) : (
            <span>
              <ChevronDownIcon className="h-6 w-6" />
            </span>
          )}
        </div>
      </div>
      <div className={(isDateRangeOpened ? '' : 'hidden') + ' mt-4'}>
        <div className={'flex'}>
          <div className={'flex-none pr-4'}>
            <div>
              <div className={'font-bold'}>From:</div>
              <div>
                <DateTimePicker
                  onChange={setStartDateTime}
                  value={startDateTime}
                  format="dd-MM-yyyy HH:mm:ss"
                  disableClock={true}
                  locale={'en-GB'}
                />
              </div>
            </div>
            <div>
              <div className={'font-bold'}>To:</div>
              <div>
                <DateTimePicker
                  onChange={setEndDateTime}
                  value={endDateTime}
                  format="dd-MM-yyyy HH:mm:ss"
                  disableClock={true}
                  locale={'en-GB'}
                />
              </div>
            </div>
            {!isValidRange && (
              <div className={'mt-2 text-red-600 text-sm'}>
                Warning: Please select dates within the same year.
              </div>
            )}
            <div className={'mt-4'}>
              <button
                type="submit"
                className={
                  'text-white xs:mt-5 xs:w-full  sm:mt-0 sm:w-36 right-2.5 bottom-2.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm p-2 mt-4 ' +
                  (!isValidRange ? 'opacity-50 cursor-not-allowed' : '')
                }
                data-test-id="e2e-apply-datetime-range"
                onClick={handleApplyDateTimeRange}
                disabled={!isValidRange}
              >
                Apply Time Range
              </button>
            </div>
          </div>
          <div className={'pl-4 flex-auto border-l-2 border-[#fff]-100'}>
            <div>
              <span>Select Year Range:</span>
            </div>
            {yearRanges.map((year) => (
              <div
                key={year}
                className={
                  quickRangeSelected === year
                    ? 'font-bold cursor-pointer'
                    : 'cursor-pointer'
                }
                onClick={() => setDateTimeRangeFromQuickRange(year)}
              >
                {year}
              </div>
            ))}
            <div
              className={
                quickRangeSelected === CUSTOM_RANGE
                  ? 'font-bold cursor-pointer'
                  : 'cursor-pointer'
              }
              onClick={() => setDateTimeRangeFromQuickRange(CUSTOM_RANGE)}
            >
              Custom Range
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
